import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import QuizPlayer from "@/components/QuizPlayer";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Sparkles, Zap, Brain, Play, Layers } from "lucide-react";
import { api, SUBJECTS, CLASSES } from "@/lib/api";
import { useAuth } from "@/lib/AuthContext";

const DIFF_COLORS = {
  easy: "text-emerald-500 bg-emerald-500/10",
  medium: "text-amber-500 bg-amber-500/10",
  hard: "text-rose-500 bg-rose-500/10",
};

export default function QuizzesPage() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [cls, setCls] = useState(user?.student_class || "10");
  const [subject, setSubject] = useState("");
  const [quizzes, setQuizzes] = useState([]);
  const [playingId, setPlayingId] = useState(null);
  const [daily, setDaily] = useState(null);
  const [xp, setXp] = useState(null);

  useEffect(() => {
    const params = { class_level: cls };
    if (subject) params.subject = subject;
    api.get("/quizzes", { params }).then((r) => setQuizzes(r.data)).catch(() => setQuizzes([]));
  }, [cls, subject]);

  useEffect(() => {
    if (!user) return;
    api.get("/daily-challenge").then((r) => setDaily(r.data)).catch(() => {});
    api.get("/me/xp").then((r) => setXp(r.data)).catch(() => {});
  }, [user, playingId]);

  if (playingId) {
    return <QuizPlayer quizId={playingId} onExit={() => setPlayingId(null)} />;
  }

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navbar />
      <div className="max-w-7xl mx-auto px-6 pt-32 pb-16">
        <div className="text-xs uppercase tracking-widest font-semibold text-primary mb-3">Interactive learning</div>
        <h1 className="font-display text-4xl md:text-5xl font-black tracking-tighter">Test what you know.</h1>
        <p className="text-muted-foreground mt-3 max-w-2xl">
          Short chapter quizzes. Instant feedback. Earn XP + certificates as you master each topic.
        </p>

        {/* XP + Daily challenge */}
        <div className="mt-10 grid grid-cols-1 md:grid-cols-3 gap-4">
          {xp && (
            <div className="glass rounded-2xl p-5 relative overflow-hidden" data-testid="xp-card">
              <div className="absolute -top-8 -right-8 w-24 h-24 rounded-full bg-orange-500/20 blur-2xl" />
              <div className="w-10 h-10 rounded-xl bg-orange-500/10 flex items-center justify-center mb-3">
                <Zap className="w-5 h-5 text-orange-500" />
              </div>
              <div className="text-xs uppercase tracking-widest font-semibold text-muted-foreground">Your XP</div>
              <div className="font-display text-3xl font-black mt-1">{xp.xp}</div>
              <div className="text-xs text-muted-foreground">
                Level {xp.level} · {xp.xp_to_next} XP to level {xp.level + 1}
              </div>
            </div>
          )}

          {daily?.quiz && (
            <div className="md:col-span-2 glass rounded-2xl p-5 relative overflow-hidden" data-testid="daily-challenge-card">
              <div className="absolute -top-10 -right-10 w-40 h-40 rounded-full bg-primary/20 blur-3xl" />
              <div className="relative flex items-center gap-4">
                <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-indigo-500 to-cyan-500 flex items-center justify-center shadow-lg shrink-0">
                  <Sparkles className="w-5 h-5 text-white" />
                </div>
                <div className="flex-1 min-w-0">
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-primary">
                    Today's challenge
                  </div>
                  <div className="font-display text-lg font-bold truncate">{daily.quiz.title}</div>
                  <div className="text-xs text-muted-foreground">
                    Class {daily.quiz.class_level} · {daily.quiz.subject} · +{daily.quiz.xp_reward} XP
                  </div>
                </div>
                <Button
                  onClick={() => setPlayingId(daily.quiz.quiz_id)}
                  data-testid="daily-play-btn"
                  className="rounded-full h-10 px-5 bg-primary hover:bg-primary/90 shrink-0"
                >
                  {daily.completed_today ? "Retry" : "Start"} <Play className="w-3.5 h-3.5 ml-1.5" fill="currentColor" />
                </Button>
              </div>
            </div>
          )}
        </div>

        {/* Filters */}
        <div className="mt-10 flex items-center gap-3 flex-wrap">
          <Select value={cls} onValueChange={setCls}>
            <SelectTrigger className="h-11 rounded-xl w-40" data-testid="quiz-class-filter">
              <SelectValue placeholder="Class" />
            </SelectTrigger>
            <SelectContent>
              {CLASSES.map((c) => (
                <SelectItem key={c} value={c}>
                  Class {c}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={subject} onValueChange={(v) => setSubject(v === "all" ? "" : v)}>
            <SelectTrigger className="h-11 rounded-xl w-52" data-testid="quiz-subject-filter">
              <SelectValue placeholder="All subjects" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All subjects</SelectItem>
              {SUBJECTS.map((s) => (
                <SelectItem key={s} value={s}>
                  {s}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Button
            variant="outline"
            onClick={() => navigate("/learn/flashcards")}
            data-testid="goto-flashcards-btn"
            className="rounded-full h-11 px-5 ml-auto"
          >
            <Layers className="w-4 h-4 mr-2" /> Flashcards
          </Button>
        </div>

        {/* Quiz grid */}
        {quizzes.length === 0 ? (
          <div className="mt-10 glass rounded-3xl p-16 text-center">
            <Brain className="w-12 h-12 text-primary mx-auto mb-4 opacity-60" />
            <div className="font-display text-2xl font-bold">No quizzes for this filter yet</div>
            <p className="text-muted-foreground mt-2">Try changing the class or subject.</p>
          </div>
        ) : (
          <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5" data-testid="quiz-grid">
            {quizzes.map((q) => (
              <div
                key={q.quiz_id}
                data-testid={`quiz-card-${q.quiz_id}`}
                className="glass rounded-2xl p-6 card-3d flex flex-col group relative overflow-hidden"
              >
                <div className="absolute -top-20 -right-20 w-40 h-40 rounded-full bg-primary/10 blur-3xl group-hover:bg-primary/20 transition-colors" />
                <div className="relative">
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-indigo-500 to-purple-500 flex items-center justify-center shadow-lg">
                      <Brain className="w-5 h-5 text-white" strokeWidth={1.8} />
                    </div>
                    <span
                      className={`text-[10px] px-2 py-0.5 rounded-full font-semibold uppercase tracking-widest ${
                        DIFF_COLORS[q.difficulty] || DIFF_COLORS.medium
                      }`}
                    >
                      {q.difficulty}
                    </span>
                  </div>
                  <div className="text-[10px] uppercase tracking-widest font-semibold text-muted-foreground">
                    Class {q.class_level} · {q.subject}
                  </div>
                  <h3 className="font-display text-lg font-bold mt-1 leading-tight line-clamp-2">{q.title}</h3>
                  <div className="text-xs text-muted-foreground mt-1">{q.chapter || "Mixed topics"}</div>
                  <div className="mt-5 flex items-center justify-between">
                    <div className="text-xs text-muted-foreground">
                      {q.question_count} Qs · +{q.xp_reward} XP
                    </div>
                    <Button
                      size="sm"
                      onClick={() => setPlayingId(q.quiz_id)}
                      data-testid={`play-quiz-${q.quiz_id}`}
                      className="rounded-full h-9 px-4 bg-primary hover:bg-primary/90"
                    >
                      Play <Play className="w-3 h-3 ml-1.5" fill="currentColor" />
                    </Button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
      <Footer />
    </div>
  );
}
